/* External Deps */
import html2pdf from 'html2pdf.js';

// Helper functions to extract data from JSON
export function extractPostalCode(address: string): string {
  if (!address) return '';
  const match = address.match(/[A-Z]\d[A-Z]\s?\d[A-Z]\d/);
  return match ? match[0] : '';
}

export function extractCompanyName(billTo: string): string {
  if (!billTo) return '';
  const lines = billTo.split('\n');
  return lines[0] || '';
}

export function extractAddress(billTo: string): string {
  if (!billTo) return '';
  const lines = billTo.split('\n');
  return lines.slice(1, -1).join(', ');
}

export function extractCountryAndZip(billTo: string): string {
  if (!billTo) return '';
  const lines = billTo.split('\n');
  const lastLine = lines[lines.length - 1];
  return lastLine || '';
}


// PDF-optimized CSS styles for the certificate
// Simplified, cleaner CSS optimized for html2pdf.js
export const certificateStylesPDFFixed = `
/* Base reset */
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

html, body {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
}

body {
    font-family: Arial, sans-serif;
    font-size: 11px;
    line-height: 1.4;
    color: #000000;
    background: #ffffff;
}

.certificate-container {
    width: 816px; /* 8.5in * 96dpi */
    margin: 0 auto;
    padding: 48px; /* 0.5in * 96dpi */
    background: #ffffff;
}

/* Header */
.header {
    text-align: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid #000000;
}

.header h1 {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.subtitle p {
    font-size: 11px;
    margin: 2px 0;
    font-style: italic;
}

/* Field groups */
.field-group {
    border: 1px solid #000000;
    margin-bottom: -1px;
    padding: 12px;
    min-height: 60px;
    background: #ffffff;
}

.field-group label {
    font-weight: bold;
    display: block;
    margin-bottom: 8px;
    font-size: 10px;
    text-transform: uppercase;
}

.field-content {
    padding-left: 15px;
}

.field-content div {
    margin: 4px 0;
    font-size: 10px;
    line-height: 1.5;
}

/* Checkboxes */
.checkbox-group {
    display: flex;
    gap: 40px;
    margin-top: 5px;
}

.checkbox-item {
    display: flex;
    align-items: center;
    gap: 8px;
}

.checkbox {
    width: 14px;
    height: 14px;
    border: 1px solid #000000;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
}

.checkbox.checked::before {
    content: '✓';
    font-size: 12px;
    font-weight: bold;
}

/* Table */
.items-table {
    width: 100%;
    border-collapse: collapse;
    margin: 10px 0;
}

.items-table th,
.items-table td {
    border: 1px solid #000000;
    padding: 8px;
    text-align: left;
    vertical-align: top;
    font-size: 9px;
}

.items-table th {
    background-color: #f0f0f0;
    font-weight: bold;
    text-align: center;
    font-size: 9px;
}

.items-table td {
    line-height: 1.4;
}

/* Date range */
.date-range {
    display: flex;
    gap: 60px;
    margin-top: 10px;
}

.date-range span {
    font-size: 10px;
}

/* Certification */
.certification-section {
    border: 1px solid #000000;
    padding: 15px;
    margin-top: 10px;
    background: #ffffff;
}

.certification-section label {
    font-weight: bold;
    display: block;
    margin-bottom: 10px;
    font-size: 11px;
}

.certification-text {
    margin: 15px 0;
    text-align: justify;
    font-size: 9px;
    line-height: 1.5;
}

.certification-text p {
    margin-bottom: 10px;
}

.signature-section {
    margin-top: 30px;
}

.signature-row {
    display: flex;
    gap: 100px;
    margin-bottom: 10px;
}

.signature-field {
    font-size: 10px;
}

.signature-note {
    font-size: 9px;
    font-style: italic;
    text-align: center;
    margin-top: 10px;
}

/* Blank lines */
.blank-line {
    display: inline-block;
    width: 200px;
    border-bottom: 1px solid #000000;
    margin-left: 5px;
}

.blank-line-short {
    width: 100px;
}

.blank-line-long {
    width: 300px;
}
`;



// Improved PDF generator class
export class ImprovedCertificateGenerator {
  static async generatePDF(certificateData: FullCertificateData): Promise<void> {
    try {
      // Generate HTML with inline styles for better compatibility
      const htmlContent = this.generateOptimizedHTML(certificateData);
      
      // Create a hidden container
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '0';
      container.style.top = '0';
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.overflow = 'auto';
      container.style.zIndex = '-9999';
      container.style.background = 'white';
      container.innerHTML = htmlContent;
      
      document.body.appendChild(container);

      // Wait for rendering
      await new Promise(resolve => setTimeout(resolve, 100));

      // Optimized options for html2pdf
      const options = {
        margin: 0, // We handle margins in CSS
        filename: `CUSMA_Certificate_${certificateData._id || 'Document'}.pdf`,
        image: { 
          type: 'jpeg', 
          quality: 1 
        },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: 816, // Fixed width
          windowWidth: 816
        },
        jsPDF: { 
          unit: 'px', 
          format: [816, 1056], // 8.5 x 11 inches in pixels
          orientation: 'portrait',
          compress: false // Better quality
        },
        pagebreak: { 
          mode: ['avoid-all', 'css'], 
          before: '.page-break-before',
          after: '.page-break-after',
          avoid: '.no-break'
        }
      };

      // Get the certificate element
      const element = container.querySelector('.certificate-container');
      if (!element) {
        throw new Error('Certificate container not found');
      }

      // Generate PDF
      await html2pdf()
        .from(element)
        .set(options)
        .save();

      // Cleanup
      document.body.removeChild(container);

    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  static generateOptimizedHTML(certificateData: FullCertificateData): string {
    const { extractedData } = certificateData;
    
    // Filter to only include items where INCLUDED === true
    const includedItems = extractedData.items?.filter(item => item.INCLUDED) || [];
    
    // Generate items rows
    const itemsRows = includedItems.length > 0 ? includedItems.map((item) => `
      <tr>
        <td style="width: 15%; text-align: center;">${item.hts || '______'}</td>
        <td style="width: 65%;">
          <div style="margin-bottom: 4px;"><strong>${item.description || 'Item Description'}</strong></div>
          <div style="font-size: 8px;">Part #: ${item.partNumber || '_____'}</div>
          <div style="font-size: 8px;">Invoice: ${item.invoiceNumber || '_____'}</div>
          <div style="font-size: 8px;">Quantity: ${item.ship || '_____'}</div>
        </td>
        <td style="width: 20%; text-align: center;">
          <div style="font-size: 9px;">Origin: ${item.origin || 'N/A'}</div>
          <div style="font-size: 9px;">Criterion: ${item.originCriterion || 'N/A'}</div>
        </td>
      </tr>
    `).join('') : '<tr><td colspan="3" style="text-align: center;">No included items</td></tr>';

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>CUSMA Certificate</title>
    <style>${certificateStylesPDFFixed}</style>
</head>
<body>
    <div class="certificate-container">
        <!-- Header -->
        <div class="header">
            <h1>CUSMA / USMCA / T-MEC CERTIFICATION OF ORIGIN</h1>
            <div class="subtitle">
                <p>Canada – United States – Mexico Agreement</p>
                <p>United States – Mexico – Canada Agreement</p>
                <p>Tratado entre México, Estados Unidos y Canadá</p>
            </div>
        </div>

        <!-- Field 1 -->
        <div class="field-group">
            <label>1. CERTIFIER</label>
            <div class="checkbox-group">
                <div class="checkbox-item">
                    <div class="checkbox ${extractedData.certifier === 'Importer' ? 'checked' : ''}"></div>
                    <span>IMPORTER</span>
                </div>
                <div class="checkbox-item">
                    <div class="checkbox ${extractedData.certifier === 'Exporter' ? 'checked' : ''}"></div>
                    <span>EXPORTER</span>
                </div>
                <div class="checkbox-item">
                    <div class="checkbox ${extractedData.certifier === 'Producer' ? 'checked' : ''}"></div>
                    <span>PRODUCER</span>
                </div>
            </div>
        </div>

        <!-- Field 2 -->
        <div class="field-group">
            <label>2. CERTIFIER DETAILS</label>
            <div class="field-content">
                <div>YOUR NAME: ${extractedData.certifierDetails?.yourName || '<span class="blank-line-long"></span>'}</div>
                <div>TITLE: ${extractedData.certifierDetails?.title || '<span class="blank-line-long"></span>'}</div>
                <div>COMPANY NAME: ${extractedData.certifierDetails?.companyName || '<span class="blank-line-long"></span>'}</div>
                <div>ADDRESS: ${extractedData.certifierDetails?.address || '<span class="blank-line-long"></span>'}</div>
                <div>COUNTRY & POSTAL/ZIP CODE: ${extractedData.certifierDetails?.countryAndPostalCode || '<span class="blank-line-long"></span>'}</div>
                <div>PHONE: ${extractedData.certifierDetails?.phone || '<span class="blank-line"></span>'}</div>
                <div>EMAIL: ${extractedData.certifierDetails?.email || '<span class="blank-line"></span>'}</div>
            </div>
        </div>

        <!-- Field 3 -->
        <div class="field-group">
            <label>3. EXPORTER DETAILS (If different than Certifier)</label>
            <div class="field-content">
                <div>COMPANY NAME: ${extractedData.exporterDetails?.companyName || 'Company Name'}</div>
                <div>ADDRESS: ${extractedData.exporterDetails?.address || 'Company Address'}</div>
                <div>COUNTRY & POSTAL/ZIP CODE: ${extractedData.exporterDetails?.countryAndPostalCode || 'Country & Postal Code'}</div>
                <div>PHONE: ${extractedData.exporterDetails?.phone || 'Phone Number'}</div>
                <div>EMAIL: ${extractedData.exporterDetails?.email || 'Email Address'}</div>
            </div>
        </div>

        <!-- Field 4 -->
        <div class="field-group">
            <label>4. PRODUCER DETAILS</label>
            <div class="field-content">
                ${extractedData.producerDetails && (extractedData.producerDetails.companyName || extractedData.producerDetails.address || extractedData.producerDetails.phone || extractedData.producerDetails.email) ? `
                <div>COMPANY NAME: ${extractedData.producerDetails.companyName || 'Company Name'}</div>
                <div>ADDRESS: ${extractedData.producerDetails.address || 'Company Address'}</div>
                <div>COUNTRY & POSTAL/ZIP CODE: ${extractedData.producerDetails.countryAndPostalCode || 'Country & Postal Code'}</div>
                <div>PHONE: ${extractedData.producerDetails.phone || 'Phone Number'}</div>
                <div>EMAIL: ${extractedData.producerDetails.email || 'Email Address'}</div>
                ` : `
                <div>COMPANY NAME: Same as exporter</div>
                <div>ADDRESS: Same as exporter</div>
                <div>COUNTRY & POSTAL/ZIP CODE: Same as exporter</div>
                <div>PHONE: Same as exporter</div>
                <div>EMAIL: Same as exporter</div>
                `}
            </div>
        </div>

        <!-- Field 5 -->
        <div class="field-group">
            <label>5. IMPORTER DETAILS (If known)</label>
            <div class="field-content">
                <div>COMPANY NAME: ${extractedData.importerDetails?.companyName || '<span class="blank-line-long"></span>'}</div>
                <div>ADDRESS: ${extractedData.importerDetails?.address || '<span class="blank-line-long"></span>'}</div>
                <div>COUNTRY & POSTAL/ZIP CODE: ${extractedData.importerDetails?.countryAndPostalCode || '<span class="blank-line-long"></span>'}</div>
                <div>PHONE: ${extractedData.importerDetails?.phone || '<span class="blank-line"></span>'}</div>
                <div>EMAIL: ${extractedData.importerDetails?.email || '<span class="blank-line"></span>'}</div>
            </div>
        </div>

        <!-- Items Table -->
        <div class="field-group">
            <label>6a, 6b & 7. GOODS DESCRIPTION AND ORIGIN CRITERIA</label>
            <table class="items-table">
                <thead>
                    <tr>
                        <th>6a. HS TARIFF<br>CLASSIFICATION<br>(6-digit level)</th>
                        <th>6b. DESCRIPTION OF THE GOOD<br>AND INVOICE NUMBER<br>(If known)</th>
                        <th>7. ORIGIN CRITERION<br>AS SET OUT IN<br>ANNEX 4.2</th>
                    </tr>
                </thead>
                <tbody>
                    ${itemsRows}
                </tbody>
            </table>
        </div>

        <!-- Field 8 -->
        <div class="field-group">
            <label>8. PERIOD COVERED BY CERTIFICATION (If applicable)</label>
            <div class="date-range">
                <span>FROM: <span class="blank-line"></span></span>
                <span>TO: <span class="blank-line"></span></span>
            </div>
        </div>

        <!-- Field 9 -->
        <div class="certification-section">
            <label>9. CERTIFICATION</label>
            <div class="certification-text">
                <p>I CERTIFY THAT THE GOODS DESCRIBED IN THIS DOCUMENT QUALIFY AS ORIGINATING AND THE INFORMATION CONTAINED IN THIS DOCUMENT IS TRUE AND ACCURATE. I ASSUME RESPONSIBILITY FOR PROVING SUCH REPRESENTATIONS AND AGREE TO MAINTAIN AND PRESENT UPON REQUEST OR TO MAKE AVAILABLE DURING A VERIFICATION VISIT, DOCUMENTATION NECESSARY TO SUPPORT THIS CERTIFICATION.</p>
                <p>THIS CERTIFICATION CONSISTS OF 1 PAGE, INCLUDING ALL ATTACHMENTS.</p>
            </div>
            <div class="signature-section">
                <div class="signature-row">
                    <div class="signature-field">SIGNATURE: <span class="blank-line-long"></span></div>
                    <div class="signature-field">DATE: <span class="blank-line"></span></div>
                </div>
                <div class="signature-note">(SIGNED BY THE CERTIFIER LISTED ABOVE)</div>
            </div>
        </div>
    </div>
</body>
</html>
    `;
  }
}


// Main function to generate certificate HTML (for preview)
export function generateCertificateHTML(certificateData: FullCertificateData): string {
  const { extractedData } = certificateData;
  
  // Filter to only include items where INCLUDED === true
  const includedItems = extractedData.items?.filter(item => item.INCLUDED) || [];
  
  const itemsTableHTML = includedItems.length > 0 ? includedItems.map((item) => `
    <tr>
      <td>${item.hts || '_'.repeat(10)}</td>
      <td>
        ${item.description || '_'.repeat(20)}<br>
        Part# ${item.partNumber || '_'.repeat(15)}<br>
        Invoice: ${item.invoiceNumber || ''}<br>
        Qty: ${item.ship || '_'.repeat(10)}
      </td>
      <td>
        <div>Origin: ${item.origin || 'N/A'}</div>
        <div>Criterion: ${item.originCriterion || 'N/A'}</div>
      </td>
    </tr>
  `).join('') : '<tr><td colspan="3" style="text-align: center;">No included items</td></tr>';

  return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>CUSMA/USMCA/T-MEC Certificate of Origin</title>
    <style>
        ${certificateStylesPDFFixed}
    </style>
</head>
<body>
    <div class="certificate-container">
        <!-- Header -->
        <div class="header">
            <h1>CUSMA / USMCA / T-MEC CERTIFICATION OF ORIGIN</h1>
            <div class="subtitle">
                <p>Canada – United States – Mexico Agreement</p>
                <p>United States – Mexico – Canada Agreement</p>
                <p>Tratado entre México, Estados Unidos y Canadá</p>
            </div>
        </div>

        <!-- Field 1: Certifier Type -->
        <div class="field-group">
            <label>1. CERTIFIER</label>
            <div class="checkbox-group">
                <div class="checkbox-item">
                    <span class="checkbox ${extractedData.certifier === 'Importer' ? 'checked' : ''}"></span>
                    <span>IMPORTER</span>
                </div>
                <div class="checkbox-item">
                    <span class="checkbox ${extractedData.certifier === 'Exporter' ? 'checked' : ''}"></span>
                    <span>EXPORTER</span>
                </div>
                <div class="checkbox-item">
                    <span class="checkbox ${extractedData.certifier === 'Producer' ? 'checked' : ''}"></span>
                    <span>PRODUCER</span>
                </div>
            </div>
        </div>

        <!-- Field 2: Certifier Details -->
        <div class="field-group">
            <label>2. CERTIFIER DETAILS</label>
            <div class="field-content">
                <div>YOUR NAME: ${extractedData.certifierDetails?.yourName || '<span class="blank-line-long"></span>'}</div>
                <div>TITLE: ${extractedData.certifierDetails?.title || '<span class="blank-line-long"></span>'}</div>
                <div>COMPANY NAME: ${extractedData.certifierDetails?.companyName || '<span class="blank-line-long"></span>'}</div>
                <div>ADDRESS: ${extractedData.certifierDetails?.address || '<span class="blank-line-long"></span>'}</div>
                <div>COUNTRY & POSTAL/ZIP CODE: ${extractedData.certifierDetails?.countryAndPostalCode || '<span class="blank-line-long"></span>'}</div>
                <div>PHONE: ${extractedData.certifierDetails?.phone || '<span class="blank-line"></span>'}</div>
                <div>EMAIL: ${extractedData.certifierDetails?.email || '<span class="blank-line"></span>'}</div>
            </div>
        </div>

        <!-- Field 3: Exporter Details -->
        <div class="field-group">
            <label>3. EXPORTER DETAILS (If different than Certifier)</label>
            <div class="field-content">
                <div>COMPANY NAME: ${extractedData.exporterDetails?.companyName || '_'.repeat(30)}</div>
                <div>ADDRESS: ${extractedData.exporterDetails?.address || '_'.repeat(40)}</div>
                <div>COUNTRY & POSTAL/ZIP CODE: ${extractedData.exporterDetails?.countryAndPostalCode || '_'.repeat(30)}</div>
                <div>PHONE: ${extractedData.exporterDetails?.phone || '_'.repeat(15)}</div>
                <div>EMAIL: ${extractedData.exporterDetails?.email || '_'.repeat(25)}</div>
            </div>
        </div>

        <!-- Field 4: Producer Details -->
        <div class="field-group">
            <label>4. PRODUCER DETAILS</label>
            <div class="field-content">
                ${extractedData.producerDetails && (extractedData.producerDetails.companyName || extractedData.producerDetails.address || extractedData.producerDetails.phone || extractedData.producerDetails.email) ? `
                <div>COMPANY NAME: ${extractedData.producerDetails.companyName || '_'.repeat(30)}</div>
                <div>ADDRESS: ${extractedData.producerDetails.address || '_'.repeat(40)}</div>
                <div>COUNTRY & POSTAL/ZIP CODE: ${extractedData.producerDetails.countryAndPostalCode || '_'.repeat(30)}</div>
                <div>PHONE: ${extractedData.producerDetails.phone || '_'.repeat(15)}</div>
                <div>EMAIL: ${extractedData.producerDetails.email || '_'.repeat(25)}</div>
                ` : `
                <div>COMPANY NAME: Same as exporter</div>
                <div>ADDRESS: Same as exporter</div>
                <div>COUNTRY & POSTAL/ZIP CODE: Same as exporter</div>
                <div>PHONE: Same as exporter</div>
                <div>EMAIL: Same as exporter</div>
                `}
            </div>
        </div>

        <!-- Field 5: Importer Details -->
        <div class="field-group">
            <label>5. IMPORTER DETAILS (If known)</label>
            <div class="field-content">
                <div>COMPANY NAME: ${extractedData.importerDetails?.companyName || '_'.repeat(30)}</div>
                <div>ADDRESS: ${extractedData.importerDetails?.address || '_'.repeat(40)}</div>
                <div>COUNTRY & POSTAL/ZIP CODE: ${extractedData.importerDetails?.countryAndPostalCode || '_'.repeat(30)}</div>
                <div>PHONE: <span class="blank-line"></span></div>
                <div>EMAIL: <span class="blank-line"></span></div>
            </div>
        </div>

        <!-- Fields 6a, 6b, 7: Items Table -->
        <div class="field-group items-section allow-page-break">
            <label>6a, 6b & 7. GOODS DESCRIPTION AND ORIGIN CRITERIA</label>
            <table class="items-table">
                <thead>
                    <tr>
                        <th style="width: 20%;">6a. HS TARIFF<br>CLASSIFICATION<br>(6-digit level)</th>
                        <th style="width: 60%;">6b. DESCRIPTION OF THE GOOD<br>AND INVOICE NUMBER<br>(If known)</th>
                        <th style="width: 20%;">7. ORIGIN CRITERION<br>AS SET OUT IN<br>ANNEX 4.2</th>
                    </tr>
                </thead>
                <tbody>
                    ${itemsTableHTML}
                </tbody>
            </table>
        </div>

        <!-- Field 8: Period Coverage -->
        <div class="field-group">
            <label>8. PERIOD COVERED BY CERTIFICATION (If applicable)</label>
            <div class="date-range">
                <span>FROM: <span class="blank-line"></span></span>
                <span>TO: <span class="blank-line"></span></span>
            </div>
        </div>

        <!-- Field 9: Certification -->
        <div class="certification-wrapper">
            <div class="certification-section no-page-break">
                <label>9. CERTIFICATION</label>
                <div class="certification-text">
                    <p>I CERTIFY THAT THE GOODS DESCRIBED IN THIS DOCUMENT QUALIFY AS ORIGINATING AND THE INFORMATION CONTAINED IN THIS DOCUMENT IS TRUE AND ACCURATE. I ASSUME RESPONSIBILITY FOR PROVING SUCH REPRESENTATIONS AND AGREE TO MAINTAIN AND PRESENT UPON REQUEST OR TO MAKE AVAILABLE DURING A VERIFICATION VISIT, DOCUMENTATION NECESSARY TO SUPPORT THIS CERTIFICATION.</p>
                    <p>THIS CERTIFICATION CONSISTS OF 1 PAGE, INCLUDING ALL ATTACHMENTS.</p>
                </div>
                <div class="signature-section">
                    <div class="signature-row">
                        <div class="signature-field">SIGNATURE: <span class="blank-line-long"></span></div>
                        <div class="signature-field">DATE: <span class="blank-line"></span></div>
                    </div>
                    <div class="signature-note">(SIGNED BY THE CERTIFIER LISTED ABOVE)</div>
                </div>
            </div>
        </div>
    </div>
</body>
</html>
  `;
}

